import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { Loader2 } from 'lucide-react';
import {
  createRegisterDocument,
  deleteRegisterDocument,
  getRegisterDocuments,
  importComprasFromConsolidado,
  importVentasFromConsolidado,
  setRegisterSaleLink,
  updateRegisterDocument,
} from '../../../../services/treasuryAPI';
import { isDateInRange, isDateInSelectedPeriod, parseDateSafe } from '../../../placeholders/periodSummaryUtils';
import AccountingRegisterTable3 from './AccountingRegisterTable3';
import AccountingDocumentSaleReconcileModal from './AccountingDocumentSaleReconcileModal';
import AccountingSalesReconcileModal from './AccountingSalesReconcileModal';
import AccountingVentasExportModal from './AccountingVentasExportModal';

const emptyForm = (registerType) => ({
  register_type: registerType,
  doc_date: new Date().toISOString().slice(0, 10),
  doc_number: '',
  document_type: '',
  counterpart_rut: '',
  counterpart_name: '',
  description: '',
  net_amount: '',
  tax_amount: '',
  exempt_amount: '0',
  total_amount: '',
  status: 'Registrado',
  notes: '',
});

const DOC_TYPE_OPTIONS = {
  ventas: ['Factura', 'Boleta electrónica', 'Nota de Crédito', 'Nota de Débito'],
  compras: ['Factura', 'Factura exenta', 'Nota de Crédito', 'Nota de Débito'],
  remuneraciones: ['Liquidación', 'Finiquito', 'Anticipo'],
  honorarios: ['Boleta de honorarios', 'Boleta de prestación de servicios'],
  invoice: ['Factura electrónica', 'Boleta electrónica', 'Guía-factura'],
  rendiciones: ['Rendición de gastos', 'Fondo por rendir', 'Viático'],
};

/**
 * Generic fiscal/operational register book (ventas, compras, etc.).
 */
const AccountingRegisterPanel = ({
  ui,
  registerType,
  title,
  isDarkMode = false,
  onBooksChanged,
  dateFrom = '',
  dateTo = '',
  selectedPeriod = null,
  documents = null,
  documentsLoading = false,
  documentsError = '',
}) => {
  const useSharedDocuments = documents !== null;
  const [rows, setRows] = useState(() => (useSharedDocuments ? documents : []));
  const [isLoading, setIsLoading] = useState(useSharedDocuments ? !!documentsLoading : true);
  const [error, setError] = useState(useSharedDocuments ? documentsError || '' : '');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(() => emptyForm(registerType));
  const [formError, setFormError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importMessage, setImportMessage] = useState('');
  const [importStatus, setImportStatus] = useState('');
  const [importElapsedSec, setImportElapsedSec] = useState(0);
  const [isLinkingSuggestedSale, setIsLinkingSuggestedSale] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showReconcileModal, setShowReconcileModal] = useState(false);
  const [reconcileDocument, setReconcileDocument] = useState(null);
  const importInputRef = useRef(null);

  useEffect(() => {
    if (!isImporting) {
      setImportElapsedSec(0);
      return undefined;
    }
    const startedAt = Date.now();
    const timer = window.setInterval(() => {
      setImportElapsedSec(Math.floor((Date.now() - startedAt) / 1000));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [isImporting]);

  const load = useCallback(async () => {
    if (useSharedDocuments) {
      return Array.isArray(documents) ? documents : [];
    }
    try {
      setIsLoading(true);
      setError('');
      const payload = await getRegisterDocuments({ register_type: registerType });
      const list = Array.isArray(payload) ? payload : [];
      setRows(list);
      return list;
    } catch (err) {
      setError(err.message || 'No se pudo cargar el libro.');
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [registerType, useSharedDocuments, documents]);

  useEffect(() => {
    if (useSharedDocuments) {
      setRows(Array.isArray(documents) ? documents : []);
      setIsLoading(!!documentsLoading);
      setError(documentsError || '');
      return;
    }
    load();
  }, [useSharedDocuments, documents, documentsLoading, documentsError, load]);

  const inputStyle = {
    height: 34,
    borderRadius: 8,
    border: `1px solid ${ui.inputBorder}`,
    background: ui.inputBg,
    color: ui.inputText,
    padding: '0 10px',
    fontSize: 13,
    width: '100%',
    boxSizing: 'border-box',
  };
  const primaryBtn = {
    height: 34,
    borderRadius: 8,
    border: 'none',
    background: ui.interactive,
    color: ui.textOnAccent || '#fff',
    padding: '0 12px',
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 600,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
  };
  const ghostBtn = {
    height: 28,
    borderRadius: 8,
    border: `1px solid ${ui.border}`,
    background: 'transparent',
    color: ui.text,
    padding: '0 8px',
    cursor: 'pointer',
    fontSize: 12,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm(registerType));
    setFormError('');
    setShowForm(true);
  };

  const openEdit = (row) => {
    setEditingId(row.id);
    setForm({
      register_type: registerType,
      doc_date: String(row.doc_date || '').slice(0, 10),
      doc_number: row.doc_number || '',
      document_type: row.document_type || '',
      counterpart_rut: row.counterpart_rut || '',
      counterpart_name: row.counterpart_name || '',
      description: row.description || '',
      net_amount: String(row.net_amount ?? ''),
      tax_amount: String(row.tax_amount ?? ''),
      exempt_amount: String(row.exempt_amount ?? '0'),
      total_amount: String(row.total_amount ?? ''),
      status: row.status || 'Registrado',
      notes: row.notes || '',
    });
    setFormError('');
    setShowForm(true);
  };

  const computedTotal = () => {
    const net = Number(form.net_amount || 0);
    const tax = Number(form.tax_amount || 0);
    const exempt = Number(form.exempt_amount || 0);
    const explicit = Number(form.total_amount || 0);
    return explicit > 0 ? explicit : net + tax + exempt;
  };

  const handleSave = async () => {
    if (!form.doc_date) {
      setFormError('La fecha es obligatoria.');
      return;
    }
    const payload = {
      ...form,
      register_type: registerType,
      net_amount: Number(form.net_amount || 0),
      tax_amount: Number(form.tax_amount || 0),
      exempt_amount: Number(form.exempt_amount || 0),
      total_amount: computedTotal(),
    };
    try {
      setIsSaving(true);
      setFormError('');
      if (editingId) await updateRegisterDocument(editingId, payload);
      else await createRegisterDocument(payload);
      setShowForm(false);
      await load();
      onBooksChanged?.();
    } catch (err) {
      setFormError(err.message || 'No se pudo guardar.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (row) => {
    try {
      await deleteRegisterDocument(row.id);
      await load();
      onBooksChanged?.();
    } catch (err) {
      window.alert(err.message || 'No se pudo anular.');
    }
  };

  const handleLinkSuggestedSale = async (row) => {
    const saleId = Number(row?.suggested_sale_id || 0);
    const docId = Number(row?.id || 0);
    if (!saleId || !docId) return;
    const saleCode = String(row?.suggested_sale_code || '').trim() || `ID ${saleId}`;
    const docLabel = row.doc_number || row.doc_code || 'documento';
    if (!window.confirm(`¿Vincular ${docLabel} con la venta ${saleCode}?`)) return;
    try {
      setIsLinkingSuggestedSale(true);
      await setRegisterSaleLink(docId, {
        sale_id: saleId,
        link_method: 'manual',
        match_score: Number(row.suggested_match_score || 0) || null,
        notes: '',
      });
      await load();
      onBooksChanged?.();
    } catch (err) {
      window.alert(err.message || 'No se pudo vincular la venta comercial.');
    } finally {
      setIsLinkingSuggestedSale(false);
    }
  };

  const linkReconciledSale = async (row) => {
    await setRegisterSaleLink(Number(row.id), {
      sale_id: Number(row.suggested_sale_id),
      link_method: 'manual',
      match_score: Number(row.suggested_match_score || 0) || null,
      notes: '',
    });
  };

  const closeReconcile = async ({ linked = 0 } = {}) => {
    setShowReconcileModal(false);
    if (linked > 0) {
      await load();
      onBooksChanged?.();
    }
  };

  const editingRow = editingId ? rows.find((row) => Number(row.id) === Number(editingId)) : null;

  const handleImportSii = () => {
    if (isImporting) return;
    setImportMessage('');
    if (importInputRef.current) {
      importInputRef.current.value = '';
      importInputRef.current.click();
    }
  };

  const handleImportFilesSelected = async (event) => {
    const selected = Array.from(event.target.files || []);
    if (!selected.length) return;

    const bookLabel = registerType === 'compras' ? 'libro de compras' : 'libro de ventas';
    const names = selected.map((file) => file.name).join(', ');
    if (
      !window.confirm(
        `¿Importar ${selected.length} archivo(s) al ${bookLabel}?\n${names}\nSe omitirán duplicados ya registrados.`
      )
    ) {
      event.target.value = '';
      return;
    }

    try {
      // Paint overlay immediately so the UI doesn't look "frozen" during long imports.
      flushSync(() => {
        setIsImporting(true);
        setImportMessage('');
        setImportStatus(
          selected.length === 1
            ? `Procesando ${selected[0].name}…`
            : `Procesando ${selected.length} archivos…`
        );
      });
      const importer =
        registerType === 'compras' ? importComprasFromConsolidado : importVentasFromConsolidado;
      const result = await importer(selected, { skip_duplicates: true });
      flushSync(() => {
        setImportStatus(`Actualizando ${bookLabel}…`);
      });
      const fileSummaries = Array.isArray(result?.files)
        ? result.files
            .map(
              (item) =>
                `${item.filename}: ${item.inserted} nuevos / ${item.skipped_duplicates} dup.`
            )
            .join(' · ')
        : '';
      let refreshed;
      if (useSharedDocuments) {
        const payload = await getRegisterDocuments({ register_type: registerType });
        refreshed = Array.isArray(payload) ? payload : [];
      } else {
        refreshed = await load();
      }
      const createdIds = new Set((result?.created_ids || []).map(Number));
      const createdRows = refreshed.filter((row) => createdIds.has(Number(row.id)));
      const hiddenByPeriod = createdRows.filter((row) => !isRowInSelectedPeriod(row)).length;
      const failedSummary = Array.isArray(result?.failed_files) && result.failed_files.length
        ? ` No se pudieron leer ${result.failed_files.length} archivo(s): ${result.failed_files
            .map((item) => `${item.filename} (${item.error})`)
            .join(' · ')}`
        : '';
      const suggestions = result?.sale_link_suggestions || {};
      const pendingLinks = registerType === 'ventas' ? Number(suggestions.pending_review_count || 0) : 0;
      const suggestionSummary = pendingLinks > 0
        ? ` ${pendingLinks} factura(s) coinciden por folio con ventas comerciales y requieren vinculación manual.`
        : '';
      setImportMessage(
        `Importación lista: ${result.inserted} nuevos, ${result.skipped_duplicates} duplicados omitidos, ${result.skipped_invalid} inválidos (${result.rows_read} filas).${
          fileSummaries ? ` ${fileSummaries}` : ''
        }${
          hiddenByPeriod
            ? ` ${hiddenByPeriod} documento(s) quedaron fuera del período seleccionado: cambia el filtro a "Todos los períodos" para verlos.`
            : ''
        }${suggestionSummary}${failedSummary}`
      );
      onBooksChanged?.();
    } catch (err) {
      setImportMessage(err.message || 'No se pudo importar el consolidado.');
    } finally {
      setIsImporting(false);
      setImportStatus('');
      event.target.value = '';
    }
  };

  const periodRange = useMemo(() => {
    if (!dateFrom && !dateTo) return null;
    const start = dateFrom ? parseDateSafe(dateFrom) : null;
    const end = dateTo ? parseDateSafe(dateTo) : null;
    if (!start && !end) return null;
    return {
      start: start || new Date(0),
      end: end || new Date(8640000000000000),
    };
  }, [dateFrom, dateTo]);

  const isRowInSelectedPeriod = useCallback(
    (row) => {
      if (selectedPeriod) return isDateInSelectedPeriod(row.doc_date, selectedPeriod);
      if (!periodRange) return true;
      return isDateInRange(row.doc_date, periodRange);
    },
    [periodRange, selectedPeriod]
  );

  const visibleRows = useMemo(() => rows.filter(isRowInSelectedPeriod), [rows, isRowInSelectedPeriod]);
  const reconcileCount = useMemo(
    () => visibleRows.filter((row) => row.has_pending_sale_match && Number(row.suggested_sale_id) > 0).length,
    [visibleRows]
  );

  const docTypes = DOC_TYPE_OPTIONS[registerType] || ['Otro'];
  const canImportSii = registerType === 'ventas' || registerType === 'compras';
  const canExportVentas = registerType === 'ventas';

  const formatElapsed = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (mins <= 0) return `${secs}s`;
    return `${mins}m ${String(secs).padStart(2, '0')}s`;
  };

  return (
    <div style={{ display: 'grid', gap: 12, position: 'relative' }}>
      {canImportSii ? (
        <input
          ref={importInputRef}
          type="file"
          accept=".csv,text/csv,text/plain"
          multiple
          style={{ display: 'none' }}
          onChange={handleImportFilesSelected}
        />
      ) : null}

      {importMessage ? (
        <p style={{ margin: 0, fontSize: 13, color: ui.muted }}>{importMessage}</p>
      ) : null}

      <AccountingRegisterTable3
        rows={visibleRows}
        loading={isLoading}
        error={error}
        isDarkMode={isDarkMode}
        tableTitle={title}
        addButtonLabel="Nuevo registro"
        onAdd={openCreate}
        onEdit={openEdit}
        onDelete={handleDelete}
        onImport={canImportSii ? handleImportSii : undefined}
        importing={isImporting}
        onExport={canExportVentas ? () => setShowExportModal(true) : undefined}
        showDocTypeTabs={registerType === 'ventas'}
        showSaleLinkColumn={registerType === 'ventas'}
        onLinkSuggestedSale={registerType === 'ventas' ? handleLinkSuggestedSale : undefined}
        linkingSuggestedSale={isLinkingSuggestedSale}
        onReconcile={canExportVentas ? () => setShowReconcileModal(true) : undefined}
        reconcileCount={reconcileCount}
        onRowClick={registerType === 'ventas' ? setReconcileDocument : undefined}
      />

      {canExportVentas ? (
        <>
          <AccountingVentasExportModal
            isOpen={showExportModal}
            onClose={() => setShowExportModal(false)}
            rows={rows}
            isDarkMode={isDarkMode}
            ui={ui}
          />
          <AccountingSalesReconcileModal
            isOpen={showReconcileModal}
            onClose={closeReconcile}
            rows={visibleRows}
            onLink={linkReconciledSale}
            isDarkMode={isDarkMode}
            ui={ui}
          />
          <AccountingDocumentSaleReconcileModal
            isOpen={Boolean(reconcileDocument)}
            document={reconcileDocument}
            onClose={() => setReconcileDocument(null)}
            isDarkMode={isDarkMode}
            ui={ui}
            onLinked={async (updated) => {
              if (updated) setReconcileDocument(updated);
              await load();
              onBooksChanged?.();
            }}
            onEditDocument={(row) => {
              setReconcileDocument(null);
              openEdit(row);
            }}
          />
        </>
      ) : null}

      {isImporting ? (
        <div
          role="status"
          aria-live="polite"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 12000,
            background: ui.overlay || 'rgba(0,0,0,0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
            boxSizing: 'border-box',
          }}
        >
          <div
            style={{
              width: 'min(420px, 100%)',
              background: ui.panel,
              border: `1px solid ${ui.border}`,
              borderRadius: 12,
              padding: '28px 24px',
              boxShadow: '0 18px 48px rgba(0,0,0,0.25)',
              display: 'grid',
              gap: 12,
              justifyItems: 'center',
              textAlign: 'center',
            }}
          >
            <Loader2
              size={36}
              color={ui.interactive}
              style={{ animation: 'accounting-import-spin 0.9s linear infinite' }}
            />
            <p style={{ margin: 0, fontSize: 16, fontWeight: 700, color: ui.text }}>
              Importando consolidado SII
            </p>
            <p style={{ margin: 0, fontSize: 13, color: ui.muted, lineHeight: 1.45 }}>
              {importStatus || 'Procesando archivos…'}
            </p>
            <p style={{ margin: 0, fontSize: 12, color: ui.textSecondary }}>
              Tiempo transcurrido: {formatElapsed(importElapsedSec)}
            </p>
            <p style={{ margin: 0, fontSize: 12, color: ui.muted, lineHeight: 1.4 }}>
              La importación puede demorar con varios archivos. No cierres ni recargues la página.
            </p>
          </div>
          <style>
            {`
              @keyframes accounting-import-spin {
                from { transform: rotate(0deg); }
                to { transform: rotate(360deg); }
              }
            `}
          </style>
        </div>
      ) : null}

      {showForm ? (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: ui.overlay || 'rgba(0,0,0,0.45)',
            zIndex: 10000,
            display: 'flex',
            justifyContent: 'flex-end',
          }}
          onClick={() => setShowForm(false)}
        >
          <div
            style={{
              width: 480,
              maxWidth: '100%',
              height: '100%',
              background: ui.panel,
              borderLeft: `1px solid ${ui.border}`,
              padding: 20,
              boxSizing: 'border-box',
              display: 'grid',
              gridTemplateRows: 'auto 1fr auto',
              gap: 16,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div>
              <p style={{ margin: 0, fontSize: 16, fontWeight: 700, color: ui.text }}>
                {editingId ? 'Editar registro' : 'Nuevo registro'}
              </p>
              <p style={{ margin: '4px 0 0', fontSize: 12, color: ui.muted }}>{title}</p>
              {editingRow?.sale_code ? (
                <p style={{ margin: '6px 0 0', fontSize: 12, fontWeight: 700, color: ui.interactive }}>
                  Venta vinculada: {editingRow.sale_code}
                </p>
              ) : editingRow?.has_pending_sale_match && editingRow?.suggested_sale_code ? (
                <div
                  style={{
                    marginTop: 8,
                    padding: '10px 12px',
                    borderRadius: 8,
                    border: `1px solid ${ui.border}`,
                    background: isDarkMode ? 'rgba(180, 83, 9, 0.12)' : '#fffbeb',
                    display: 'grid',
                    gap: 8,
                  }}
                >
                  <div style={{ fontSize: 12, color: ui.text }}>
                    <strong>Match con venta comercial:</strong> {editingRow.suggested_sale_code}
                    {Number(editingRow.suggested_match_score || 0) > 0
                      ? ` · ${Math.round(Number(editingRow.suggested_match_score))}% coincidencia`
                      : ''}
                  </div>
                  <button
                    type="button"
                    onClick={() => handleLinkSuggestedSale(editingRow)}
                    disabled={isLinkingSuggestedSale}
                    style={primaryBtn}
                  >
                    {isLinkingSuggestedSale ? 'Vinculando…' : 'Vincular venta comercial'}
                  </button>
                </div>
              ) : null}
            </div>
            <div style={{ overflowY: 'auto', display: 'grid', gap: 10, alignContent: 'start' }}>
              <label style={{ display: 'grid', gap: 6, fontSize: 12, color: ui.textSecondary }}>
                Fecha
                <input
                  type="date"
                  value={form.doc_date}
                  onChange={(e) => setForm((p) => ({ ...p, doc_date: e.target.value }))}
                  style={inputStyle}
                />
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <label style={{ display: 'grid', gap: 6, fontSize: 12, color: ui.textSecondary }}>
                  Nº documento
                  <input
                    value={form.doc_number}
                    onChange={(e) => setForm((p) => ({ ...p, doc_number: e.target.value }))}
                    style={inputStyle}
                  />
                </label>
                <label style={{ display: 'grid', gap: 6, fontSize: 12, color: ui.textSecondary }}>
                  Tipo
                  <select
                    value={form.document_type}
                    onChange={(e) => setForm((p) => ({ ...p, document_type: e.target.value }))}
                    style={inputStyle}
                  >
                    <option value="">Selecciona</option>
                    {docTypes.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <label style={{ display: 'grid', gap: 6, fontSize: 12, color: ui.textSecondary }}>
                RUT contraparte
                <input
                  value={form.counterpart_rut}
                  onChange={(e) => setForm((p) => ({ ...p, counterpart_rut: e.target.value }))}
                  style={inputStyle}
                />
              </label>
              <label style={{ display: 'grid', gap: 6, fontSize: 12, color: ui.textSecondary }}>
                Nombre contraparte
                <input
                  value={form.counterpart_name}
                  onChange={(e) => setForm((p) => ({ ...p, counterpart_name: e.target.value }))}
                  style={inputStyle}
                />
              </label>
              <label style={{ display: 'grid', gap: 6, fontSize: 12, color: ui.textSecondary }}>
                Descripción
                <input
                  value={form.description}
                  onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                  style={inputStyle}
                />
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                <label style={{ display: 'grid', gap: 6, fontSize: 12, color: ui.textSecondary }}>
                  Neto
                  <input
                    type="number"
                    value={form.net_amount}
                    onChange={(e) => setForm((p) => ({ ...p, net_amount: e.target.value }))}
                    style={inputStyle}
                  />
                </label>
                <label style={{ display: 'grid', gap: 6, fontSize: 12, color: ui.textSecondary }}>
                  IVA
                  <input
                    type="number"
                    value={form.tax_amount}
                    onChange={(e) => setForm((p) => ({ ...p, tax_amount: e.target.value }))}
                    style={inputStyle}
                  />
                </label>
                <label style={{ display: 'grid', gap: 6, fontSize: 12, color: ui.textSecondary }}>
                  Exento
                  <input
                    type="number"
                    value={form.exempt_amount}
                    onChange={(e) => setForm((p) => ({ ...p, exempt_amount: e.target.value }))}
                    style={inputStyle}
                  />
                </label>
              </div>
              <label style={{ display: 'grid', gap: 6, fontSize: 12, color: ui.textSecondary }}>
                Total (opcional; si vacío = neto+IVA+exento)
                <input
                  type="number"
                  value={form.total_amount}
                  onChange={(e) => setForm((p) => ({ ...p, total_amount: e.target.value }))}
                  style={inputStyle}
                />
              </label>
              <label style={{ display: 'grid', gap: 6, fontSize: 12, color: ui.textSecondary }}>
                Estado
                <select
                  value={form.status}
                  onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))}
                  style={inputStyle}
                >
                  <option value="Registrado">Registrado</option>
                  <option value="Contabilizado">Contabilizado</option>
                  <option value="Anulada">Anulada</option>
                  <option value="Anulado">Anulado</option>
                </select>
              </label>
              {formError ? <p style={{ margin: 0, color: ui.danger, fontSize: 12 }}>{formError}</p> : null}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button type="button" style={{ ...ghostBtn, height: 34 }} onClick={() => setShowForm(false)}>
                Cancelar
              </button>
              <button type="button" style={primaryBtn} onClick={handleSave} disabled={isSaving}>
                {isSaving ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default AccountingRegisterPanel;
